import AppBanner from '@/components/AppBanner'
import BANNER_TEXT_VALUES from '@/components/AppBanner/constants'
import Cobrand from '@/components/Cobrand'
import Flight from '@/components/Flights'
import Layout from '@/components/Layout'
import Meta from '@/components/Meta'
import SchemaMarkup from '@/components/SchemaMarkup/SchemaMarkup'
import TmsScript from '@/components/TmsScript/TmsScript'
import FlightSchemaMarkup from '@/constants/flight-schema-markup'
import DEFAULT_META_TAGS from '@/constants/meta'
import { FLIGHT_PRODUCT_TYPE } from '@/constants/products'
import useCanonicalUrl from '@/hooks/useCanonicalUrl'
import type { ContentPageProps, CustomContext } from '@/types'
import { ContentfulProvider } from '@pcln/contentful-components'
import { FeatureProvider } from '@pcln/feature-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type GetServerSideProps } from 'next'
import { useState } from 'react'
import setiService from '../server/middleware/seti'
import prePopulateFlightValues from '../server/prepop/flights'
import getContentfulData from '../server/utils/get-contentful-data'
import getFlightDetails, { extractFlightDetailsQueryParams } from '../server/utils/get-flight-details'
import getSkymondeData from '../server/utils/get-skymonde-data'
import getXPclnHeaders from '../server/utils/get-x-pcln-headers'
import oktaJwtVerify from '../server/utils/okta-jwt-verifier'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import retrieveContentfulSecrets from '../server/utils/retrieve-contentful-secrets'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import mapSetiResponse from '../utils/map-seti-response'

function Flights({ content, headerHTML, footerHTML, bootstrapData }: ContentPageProps) {
  const { featureToggles } = bootstrapData
  const canonicalUrl = useCanonicalUrl()
  const [queryClient] = useState(() => new QueryClient())
  return (
    <>
      <Meta
        description={DEFAULT_META_TAGS.description.flights}
        pageTitle={DEFAULT_META_TAGS.title.flights}
        canonicalUrl={canonicalUrl}
      />
      <TmsScript product='flights' />
      <SchemaMarkup schema={FlightSchemaMarkup} />
      <FeatureProvider features={featureToggles}>
        <ContentfulProvider contentful={content}>
          <QueryClientProvider client={queryClient}>
            <AppBanner content={BANNER_TEXT_VALUES[FLIGHT_PRODUCT_TYPE]} />
            <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
              <Cobrand />
              <Flight />
            </Layout>
          </QueryClientProvider>
        </ContentfulProvider>
      </FeatureProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<ContentPageProps> = async (ctx) => {
  const { req, res } = ctx as CustomContext
  const bootstrapData = generateBootstrapData({ product: 'flights', req, res })
  const { webstats, referral, appVersion, appName, locale } = bootstrapData
  const pricelineSpecificHeaders = getXPclnHeaders(req.headers)
  const { clientGUID: cguid } = webstats
  const pclnApolloHeaders = {
    ...pricelineSpecificHeaders,
    'apollographql-client-name': appName,
    'apollographql-client-version': appVersion,
    'x-pcln-cguid': cguid,
  } as const
  const { contentfulDeliverySecret, contentfulPreviewSecret } = retrieveContentfulSecrets()
  const rebookOrModifyQueryParams = extractFlightDetailsQueryParams(ctx.query)
  const isContentfulEnabled = res.locals.features?.siteExperience?.isContentfulEnabled ?? true
  const isSkymondeEnabled = res.locals.features?.siteExperience?.isSkymondeEnabled ?? false
  const [
    flightPrePopData,
    pclnHeaderFooterContent,
    signInStatus,
    { content, experimentNames },
    flightRebookOrModifyDetails,
    skymondeSEO,
  ] = await Promise.all([
    prePopulateFlightValues(ctx.query),
    fetchPCLNHeaderFooterContent(req, res),
    oktaJwtVerify({ authHdr: req.headers.authorization ?? '', cguid }),
    getContentfulData({
      contentTypeId: 'pageLanding',
      contentfulDeliverySecret,
      contentfulPreviewSecret,
      product: 'Flight',
      contentPreview: ctx.query['content-preview'] === 'true',
      isContentfulEnabled,
    }),
    rebookOrModifyQueryParams
      ? getFlightDetails(rebookOrModifyQueryParams, pclnApolloHeaders)
      : Promise.resolve(null),
    getSkymondeData('flights', locale.localeCode, isSkymondeEnabled),
  ])
  const setiResponseEvents = await setiService(req, {
    appVersion,
    allowSetiOverrides: true,
    webstats,
    referral,
    reqQueryParams: ctx.query,
    ...(experimentNames && {
      additionalExperiments: experimentNames,
    }),
  })
  const prePopData = flightPrePopData ? { flights: flightPrePopData } : null

  const bootstrapWithJwtSignInData = {
    ...bootstrapData,
    customer: {
      signedIn: signInStatus.signedIn ?? false,
      emailAddress: signInStatus.email ?? '',
      firstName: String(signInStatus.firstName ?? ''),
    },
  }

  return {
    props: {
      content,
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapWithJwtSignInData,
        signInStatus,
        fullSETI: setiResponseEvents,
        seti: mapSetiResponse(setiResponseEvents),
        ...(flightRebookOrModifyDetails && { flightRebookOrModifyDetails }),
        ...(prePopData && { prePopData }),
        skymondeSEO,
      },
    },
  }
}

export default Flights
